import AddIcon from "@mui/icons-material/Add";
import HistoryIcon from "@mui/icons-material/History";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import React from "react";
import { Link } from "react-router-dom";
import { onGuestShareClick } from "../lib/guest-share-click";
import { clockTime, pendingLabel } from "../lib/sync-state";
import { DemoBadge } from "./DemoBadge";
import { Button, buttonClassName } from "./ui/Button";

export type CollectionHeadingSync = {
  lastSyncedAt: string | null;
  pending: number;
  failed: number;
  pausedUntil?: string | null;
  /** The list answer came from the read cache after a Google error. */
  stale?: boolean;
  staleFetchedAt?: string | null;
};

type CollectionHeadingProps = {
  title: string;
  total: number;
  loading: boolean;
  demoMode?: boolean;
  /** Sheets sync state (ARD 9.6). Omitted for other datasources. */
  sync?: CollectionHeadingSync;
  /**
   * Desktop Manager owns New record. Phone search already draws a full-width
   * one, so the collection route omits this on 390 and keeps a single button.
   */
  onCreate?: () => void;
  /**
   * Route of the members page. Set for the owner of a real panel, omitted for
   * the demo and for an invited member, who has nobody to share with.
   */
  shareHref?: string;
  /**
   * Route of the Activity list (MOSA-368 slice 1). Set for the owner of a real
   * panel; a member reads a row's own history in the record drawer instead.
   */
  activityHref?: string;
  className?: string;
};

/**
 * The collection name, the record count, and the demo warning. Phone used to
 * swap the whole Manager for search and lose this row, so a guest saw write
 * checkboxes and no "changes are not saved". One h1, shared by both layouts.
 *
 * The sync line is the only live region. Row markers stay quiet so a bulk edit
 * does not read every row out loud.
 */
export const CollectionHeading: React.FC<CollectionHeadingProps> = ({
  title,
  total,
  loading,
  demoMode = false,
  sync,
  onCreate,
  shareHref,
  activityHref,
  className = "",
}) => {
  const count =
    loading && !total
      ? "Loading…"
      : `${total.toLocaleString()} ${total === 1 ? "record" : "records"}`;

  const now = new Date();
  const lastSynced = clockTime(sync?.lastSyncedAt, now);
  const staleFrom = clockTime(sync?.staleFetchedAt, now);
  const pausedUntil = clockTime(sync?.pausedUntil, now);
  const pending = pendingLabel(sync?.pending ?? 0);
  const failed = sync?.failed ?? 0;

  const items: string[] = [];
  if (lastSynced) items.push(`Last synced ${lastSynced}`);
  if (pending) items.push(pending);
  if (failed > 0) items.push(failed === 1 ? "1 failed change" : `${failed} failed changes`);
  if (pausedUntil) items.push(`Paused by Google until ${pausedUntil}`);
  if (sync?.stale) {
    items.push(
      staleFrom ? `Showing data from ${staleFrom}` : "Showing saved data"
    );
  }

  return (
    <header
      data-testid="collection-heading"
      className={`flex shrink-0 flex-wrap items-center justify-between gap-3 ${className}`}
    >
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-2.5 gap-y-1">
        <h1 className="m-0 break-words text-lg font-semibold text-od-text">
          {title}
        </h1>
        <span className="shrink-0 text-xs text-od-muted">{count}</span>
        {demoMode && <DemoBadge />}
        {items.length > 0 && (
          <span
            data-testid="collection-sync-status"
            aria-live="polite"
            aria-atomic="true"
            className="flex min-w-0 flex-wrap items-baseline gap-x-2.5 gap-y-1 text-xs"
          >
            {items.map((item) => (
              <span
                key={item}
                className={
                  item.startsWith("Showing data from") ||
                  item.startsWith("Showing saved data") ||
                  item.startsWith("Paused by Google") ||
                  item.includes("failed change")
                    ? "text-od-warning"
                    : "text-od-muted"
                }
              >
                {item}
              </span>
            ))}
          </span>
        )}
      </div>

      {(shareHref || activityHref || onCreate) && (
        <div className="flex shrink-0 items-center gap-2">
          {/* A Link, not a Button with navigate: Share is a destination, so
              it opens in a new tab and copies as a URL like any other. */}
          {shareHref && (
            <Link
              to={shareHref}
              className={buttonClassName("secondary", "sm")}
              data-testid="share-panel"
              onClick={() => onGuestShareClick(shareHref)}
            >
              <PersonAddAltIcon sx={{ fontSize: 16 }} /> Share
            </Link>
          )}
          {activityHref && (
            <Link
              to={activityHref}
              className={buttonClassName("secondary", "sm")}
              data-testid="panel-activity"
            >
              <HistoryIcon sx={{ fontSize: 16 }} /> Activity
            </Link>
          )}
          {onCreate && (
            <Button
              onClick={onCreate}
              variant="secondary" size="sm" className="od-new-record border-od-primary font-semibold text-od-primary-text hover:bg-transparent hover:opacity-90"
            >
              <AddIcon sx={{ fontSize: 16 }} /> New record
            </Button>
          )}
        </div>
      )}
    </header>
  );
};
